import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Zap,
  Calendar,
  ArrowRight,
  Lock,
  Code2,
  Copy,
  Check,
  Flame,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Crown,
  Target,
  MapPin,
  Brain,
  Search,
  HelpCircle,
  Sparkles,
  FileCode2,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslations, translateSeverity } from "@/lib/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ImpactBadge = ({ level }) => {
  const cls = {
    high: "bg-green-500/15 text-green-400 border-green-500/20",
    medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
    low: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  };
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-widest rounded-sm px-1.5 py-0 ${cls[level] || cls.low}`}
    >
      {level}
    </Badge>
  );
};

const EffortBadge = ({ level }) => {
  const cls = {
    low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    medium: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    high: "bg-red-500/15 text-red-400 border-red-500/20",
  };
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-widest rounded-sm px-1.5 py-0 ${cls[level] || cls.medium}`}
    >
      {level} effort
    </Badge>
  );
};

// ─── CodeBlock with copy button ───────────────────────────────────────────────

function CodeBlock({ code, language = "html", lang = "en" }) {
  const t = useTranslations(lang);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      toast.success(t('copied'));
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="relative rounded-lg overflow-hidden" style={{ background: "#0D1117" }}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white transition-colors" data-testid="copy-code-btn">
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? t('copied') : t('copy')}
        </button>
      </div>
      <pre className="p-4 text-[11px] leading-relaxed text-zinc-300 overflow-x-auto" style={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
        {code}
      </pre>
    </div>
  );
}

// ─── How-to-deploy modal ──────────────────────────────────────────────────────

function HowToDeployModal({ fix, open, onClose }) {
  if (!fix) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" style={{ background: "#0B0F14", border: "1px solid rgba(255,255,255,0.08)" }}>
        <DialogHeader>
          <DialogTitle className="text-white text-base flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-400" />
            How to deploy in 60 seconds
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">{fix.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {(fix.how_to_deploy || "").split("\n").filter(Boolean).map((step, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold text-white" style={{ background: "rgba(59,130,246,0.3)", border: "1px solid rgba(59,130,246,0.4)" }}>
                {i + 1}
              </span>
              <p className="text-sm text-zinc-300 leading-relaxed">{step.replace(/^\d+\.\s*/, "")}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Paywall Dialog (FREE users) ──────────────────────────────────────────────

function PaywallDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md text-center" style={{ background: "#0B0F14", border: "1px solid rgba(245,158,11,0.2)" }}>
        <DialogHeader>
          <DialogTitle className="text-white text-lg flex items-center justify-center gap-2">
            <Lock className="w-5 h-5 text-amber-400" />
            Unlock ready-to-paste solutions
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm mt-2 leading-relaxed">
            Access to ready-to-paste solutions (Copy-Paste) requires the CORE plan.
            Unlock access to deploy fixes instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 p-4 rounded-xl" style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)" }}>
          <div className="flex items-center gap-2 mb-3 justify-center">
            <Crown className="w-5 h-5 text-amber-400" />
            <span className="text-sm font-semibold text-amber-300">CORE Plan — 19 GBP/mo</span>
          </div>
          <ul className="text-xs text-zinc-400 space-y-1.5 text-left">
            <li className="flex items-center gap-2"><Check className="w-3 h-3 text-green-400 shrink-0" /> Ready-to-deploy code (Schema, robots, FAQ)</li>
            <li className="flex items-center gap-2"><Check className="w-3 h-3 text-green-400 shrink-0" /> 30 audits per month</li>
            <li className="flex items-center gap-2"><Check className="w-3 h-3 text-green-400 shrink-0" /> Full Action Plan with priorities</li>
            <li className="flex items-center gap-2"><Check className="w-3 h-3 text-green-400 shrink-0" /> PDF Export</li>
          </ul>
        </div>
        <Link to="/pricing" className="block mt-4">
          <Button className="w-full text-white font-semibold" style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)" }} data-testid="paywall-upgrade-btn">
            <Crown className="w-4 h-4 mr-2" /> Unlock access
          </Button>
        </Link>
      </DialogContent>
    </Dialog>
  );
}

// ─── Market Intelligence Section ──────────────────────────────────────────────

export function MarketIntelligenceSection({ mi, language = 'en' }) {
  const t = useTranslations(language);
  if (!mi || !mi.identified_entity) return null;

  const entity = mi.identified_entity;
  const queries = mi.persona_queries || [];
  const gaps = mi.market_context_gaps || '';
  const keywords = mi.perfect_keywords || [];

  return (
    <div
      className="rounded-2xl p-5 sm:p-7 mb-6"
      style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.06))', border: '1px solid rgba(139,92,246,0.2)' }}
      data-testid="market-intelligence-section"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.2)' }}>
          <Brain className="w-4 h-4 text-purple-400" />
        </div>
        <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">{t('market_intelligence')}</h3>
      </div>

      {/* Entity identification */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {entity.niche && (
          <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Target className="w-4 h-4 text-blue-400 shrink-0" />
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest block">{t('niche')}</span>
              <span className="text-sm text-white font-medium">{entity.niche}</span>
            </div>
          </div>
        )}
        {entity.location && (
          <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <MapPin className="w-4 h-4 text-green-400 shrink-0" />
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest block">{t('location')}</span>
              <span className="text-sm text-white font-medium">{entity.location}</span>
            </div>
          </div>
        )}
        {entity.goal && (
          <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <TrendingUp className="w-4 h-4 text-amber-400 shrink-0" />
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest block">{t('business_goal')}</span>
              <span className="text-sm text-white font-medium">{entity.goal}</span>
            </div>
          </div>
        )}
      </div>

      {/* Persona Queries */}
      {queries.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-cyan-400 uppercase tracking-widest font-semibold">{t('customer_questions_ai')}</span>
          </div>
          <div className="space-y-2">
            {queries.map((q, i) => (
              <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={{ background: 'rgba(6,182,212,0.05)', border: '1px solid rgba(6,182,212,0.1)' }}>
                <Search className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">{q}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Market Context Gaps */}
      {gaps && (
        <div className="mb-5 p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.12)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-red-400" />
            <span className="text-xs text-red-400 uppercase tracking-widest font-semibold">{t('why_ai_doesnt_cite')}</span>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">{gaps}</p>
        </div>
      )}

      {/* Perfect Keywords */}
      {keywords.length > 0 && (
        <div>
          <span className="text-xs text-zinc-500 uppercase tracking-widest font-semibold mb-2 block">{t('ideal_keywords')}</span>
          <div className="flex flex-wrap gap-2">
            {keywords.map((kw, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', color: '#C4B5FD' }}>
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Single Fix Card ──────────────────────────────────────────────────────────

function FixCard({ fix }) {
  const [expanded, setExpanded] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const typeLabel = fix.type === "code_prompt" ? "AI prompt" : fix.type === "text" || fix.type === "code_text" ? "text" : "html";

  return (
    <div className="rounded-xl p-4 sm:p-5" style={{ background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.12)" }}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Code2 className="w-4 h-4 text-blue-400 shrink-0" />
            <h5 className="text-sm font-semibold text-white">{fix.title}</h5>
            <Badge variant="outline" className={`text-[10px] uppercase px-1.5 py-0 rounded-sm ${fix.priority === "high" ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}>
              {fix.priority}
            </Badge>
          </div>
          <p className="text-xs text-zinc-400 mt-1">{fix.description}</p>
          {fix.impact && (
            <p className="text-xs text-emerald-400/80 mt-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {fix.impact}
            </p>
          )}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 transition-colors shrink-0 mt-0.5" data-testid={`fix-toggle-${fix.id}`}>
          {expanded ? (<>Hide <ChevronUp className="w-3.5 h-3.5" /></>) : (<>Show solution <ChevronDown className="w-3.5 h-3.5" /></>)}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-3">
          <CodeBlock code={fix.code} language={typeLabel} />
          <button onClick={() => setShowHowTo(true)} className="flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-blue-300 transition-colors">
            <BookOpen className="w-3.5 h-3.5" />
            How to deploy in 60 seconds
          </button>
          <HowToDeployModal fix={fix} open={showHowTo} onClose={() => setShowHowTo(false)} />
        </div>
      )}
    </div>
  );
}

// ─── Fixes Panel ──────────────────────────────────────────────────────────────

export function FixesPanel({ auditId, canViewCodeFixes }) {
  const [fixes, setFixes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const loadFixes = async () => {
    if (fixes) { setRevealed(!revealed); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/audit/${auditId}/fixes`);
      setFixes(res.data);
      setRevealed(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to load fixes.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!canViewCodeFixes) {
    return (
      <div className="mt-4 p-4 rounded-xl flex flex-col sm:flex-row items-center gap-3" style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)" }}>
        <div className="flex items-center gap-2 flex-1">
          <Lock className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-amber-400">PRO Code</span> — Schema.org, robots.txt and FAQ markup ready to paste.
          </p>
        </div>
        <Link to="/pricing">
          <Button disabled className="text-white font-medium px-4 py-2 rounded-lg text-sm opacity-70 cursor-not-allowed" style={{ background: "#F59E0B" }}>
            <Crown className="w-3.5 h-3.5 mr-1.5" /> Unlock PRO code
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-4 mb-8">
      <button onClick={loadFixes} disabled={loading} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.3)" }} data-testid="load-fixes-btn">
        <Code2 className="w-4 h-4 text-blue-400" />
        {loading ? "Generating code..." : revealed ? "Hide generated code" : "Show ready-to-deploy code"}
        {!loading && !revealed && <ArrowRight className="w-3.5 h-3.5 text-blue-400" />}
      </button>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
      {revealed && fixes?.fixes?.length > 0 && (
        <div className="mt-4 space-y-3">
          {fixes.fixes.map((fix) => <FixCard key={fix.id} fix={fix} />)}
        </div>
      )}
    </div>
  );
}

// ─── Per-action "Show Solution" Button ────────────────────────────────────────

function ActionFixButton({ action, auditId, canViewCodeFixes, language = 'en' }) {
  const t = useTranslations(language);
  const [solution, setSolution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [error, setError] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  // Support both old issue_key, new required_fix_type, and action.id (from top_actions)
  const fixType = action.required_fix_type || action.issue_key || action.id;
  const isFixable = fixType && fixType !== 'none' && (action.is_fixable !== false);
  if (!isFixable) return null;

  const handleShowCode = () => {
    if (!canViewCodeFixes) {
      setShowPaywall(true);
      return;
    }
    fetchSolution();
  };

  const fetchSolution = async () => {
    if (solution) { setExpanded(!expanded); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/audit/${auditId}/fixes/${fixType}`);
      setSolution(res.data);
      setExpanded(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
      } else {
        const msg = err.response?.data?.detail || 'Failed to load solution.';
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        onClick={handleShowCode}
        disabled={loading}
        className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg transition-all hover:brightness-110 disabled:opacity-60"
        style={{
          background: canViewCodeFixes ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.12)',
          border: `1px solid ${canViewCodeFixes ? 'rgba(59,130,246,0.25)' : 'rgba(245,158,11,0.25)'}`,
          color: canViewCodeFixes ? '#93C5FD' : '#F59E0B'
        }}
        data-testid={`action-fix-btn-${fixType}`}
      >
        {canViewCodeFixes ? <Code2 className="w-3 h-3" /> : <Crown className="w-3 h-3" />}
        {loading ? '...' : expanded ? t('hide_code') : t('show_solution')}
        {!loading && !expanded && <ChevronDown className="w-3 h-3" />}
        {!loading && expanded && <ChevronUp className="w-3 h-3" />}
      </button>
      {error && <p className="text-[10px] text-red-400 mt-1">{error}</p>}
      {expanded && solution && (
        <div className="mt-2 space-y-2">
          <CodeBlock code={solution.code} language={solution.type === 'code_prompt' ? 'AI prompt' : solution.type === 'code_text' ? 'text' : 'html'} />
          {solution.how_to_deploy && (
            <button onClick={() => setShowHowTo(true)} className="flex items-center gap-1 text-[10px] text-zinc-400 hover:text-blue-300 transition-colors">
              <BookOpen className="w-3 h-3" /> {t('how_to_deploy')}
            </button>
          )}
          <HowToDeployModal fix={solution} open={showHowTo} onClose={() => setShowHowTo(false)} />
        </div>
      )}
      <PaywallDialog open={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
}

// ─── MI Action Item (new V4 format) ──────────────────────────────────────────

function MIActionItem({ action, index, auditId, canViewCodeFixes, language = 'en' }) {
  const t = useTranslations(language);
  const fixType = action.required_fix_type || 'none';
  const hasCode = fixType !== 'none';

  return (
    <div
      className="p-4 sm:p-5 rounded-xl"
      style={{
        background: action.priority === 1 ? 'rgba(239,68,68,0.06)' : action.priority === 2 ? 'rgba(245,158,11,0.06)' : 'rgba(59,130,246,0.06)',
        border: `1px solid ${action.priority === 1 ? 'rgba(239,68,68,0.15)' : action.priority === 2 ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)'}`,
      }}
      data-testid={`mi-action-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white shrink-0"
              style={{ background: action.priority === 1 ? 'rgba(239,68,68,0.3)' : action.priority === 2 ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)' }}>
              P{action.priority}
            </span>
            <h4 className="text-sm font-semibold text-white">{action.title}</h4>
            {hasCode && (
              <Badge variant="outline" className="text-[10px] rounded-sm px-1.5 py-0 bg-blue-500/10 text-blue-400 border-blue-500/20">
                {t('auto_fix')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-zinc-400 mb-1">{action.problem}</p>
          {action.target_url && action.target_url !== 'site-wide' && (
            <p className="text-xs text-blue-400 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> {action.target_url}
            </p>
          )}
        </div>
      </div>
      {hasCode && auditId && (
        <ActionFixButton action={action} auditId={auditId} canViewCodeFixes={canViewCodeFixes} language={language} />
      )}
    </div>
  );
}

// ─── GEO AI Visibility Check Section ──────────────────────────────────────────

export function GeoVisibilitySection({ geoCheck, language = 'en' }) {
  const t = useTranslations(language);
  if (!geoCheck || !geoCheck.questions?.length) return null;
  const score = geoCheck.score || 0;
  const found = geoCheck.found_count || 0;
  const total = geoCheck.total_questions || geoCheck.questions.length;

  const scoreColor = score >= 70 ? 'text-green-400' : score >= 40 ? 'text-yellow-400' : 'text-red-400';
  const scoreBg = score >= 70 ? 'rgba(34,197,94,0.1)' : score >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';
  const scoreBorder = score >= 70 ? 'rgba(34,197,94,0.2)' : score >= 40 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)';

  return (
    <div
      className="rounded-2xl p-5 sm:p-7 mb-6"
      style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.15)' }}
      data-testid="geo-visibility-section"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(6,182,212,0.2)' }}>
            <Brain className="w-4 h-4 text-cyan-400" />
          </div>
          <h3 className="text-sm font-semibold text-cyan-300 uppercase tracking-wider">{t('how_ai_sees_you')}</h3>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: scoreBg, border: `1px solid ${scoreBorder}` }}>
          <span className={`text-lg font-bold ${scoreColor}`}>{score}%</span>
          <span className="text-xs text-zinc-500">({found}/{total})</span>
        </div>
      </div>

      <div className="space-y-2">
        {geoCheck.questions.map((q, i) => (
          <div
            key={i}
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{
              background: q.status === 'found' ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)',
              border: `1px solid ${q.status === 'found' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'}`,
            }}
            data-testid={`geo-question-${i}`}
          >
            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${q.status === 'found' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {q.status === 'found' ? <Check className="w-3 h-3" /> : '!'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-300">{q.question}</p>
              {q.answer && q.status === 'found' && (
                <p className="text-xs text-emerald-400/70 mt-1 italic leading-relaxed">→ {q.answer}</p>
              )}
              {q.status === 'missing' && (
                <p className="text-xs text-red-400/60 mt-1 italic">{t('ai_cannot_find')}</p>
              )}
              <span className={`text-[10px] uppercase tracking-widest ${q.status === 'found' ? 'text-green-400' : 'text-red-400'}`}>
                {q.status === 'found' ? t('found') : t('missing')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main ActionPlan component ────────────────────────────────────────────────

const ActionItem = ({ action, auditId, canViewCodeFixes, language = 'en' }) => {
  const t = useTranslations(language);
  return (
  <Card className="bg-[#0D0D0D] border-white/10 hover:border-white/20 transition-colors" data-testid={`action-${action.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-sm bg-blue-600/20 flex items-center justify-center shrink-0 mt-0.5">
          <span className="font-mono-data text-xs text-blue-400 font-bold">{action.priority}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-sm font-semibold text-white">{action.title}</h4>
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest rounded-sm px-1.5 py-0 bg-white/5 text-zinc-400 border-white/10">
              {action.effort}
            </Badge>
            {action.is_fixable && (
              <Badge variant="outline" className="text-[10px] rounded-sm px-1.5 py-0 bg-blue-500/10 text-blue-400 border-blue-500/20">
                {t('auto_fix')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-zinc-400 mb-2">{action.why_it_matters}</p>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="text-zinc-500 uppercase tracking-widest">{t('impact')}:</span>
            <span className="text-zinc-500">SEO</span> <ImpactBadge level={action.expected_impact?.seo} />
            <span className="text-zinc-500">AI</span> <ImpactBadge level={action.expected_impact?.ai} />
          </div>
          {action.where?.url && (
            <div className="flex items-center gap-1 mt-2 text-xs text-blue-400">
              <ArrowRight className="w-3 h-3" /> <span>{action.where.url}</span>
            </div>
          )}
          {action.acceptance_criteria?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <span className="text-[10px] text-green-400 uppercase tracking-widest">{t('acceptance_criteria')}</span>
              <ul className="mt-1 space-y-0.5">
                {action.acceptance_criteria.map((c, i) => (
                  <li key={i} className="text-xs text-zinc-400 flex items-start gap-1">
                    <span className="text-green-400 shrink-0">ok</span> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {action.example_structure?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileCode2 className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] text-purple-400 uppercase tracking-widest">{t('ready_to_deploy_brief')}</span>
              </div>
              <div className="bg-purple-500/5 border border-purple-500/10 rounded p-3">
                {action.example_structure.map((line, i) => {
                  const isHeading = line.startsWith('H1:') || line.startsWith('H2:') || line.startsWith('H3:');
                  return (
                    <p key={i} className={`text-xs leading-relaxed ${isHeading ? 'text-purple-300 font-semibold mt-1.5 first:mt-0' : 'text-zinc-400 pl-3'}`}>
                      {line}
                    </p>
                  );
                })}
              </div>
            </div>
          )}
          {auditId && <ActionFixButton action={action} auditId={auditId} canViewCodeFixes={canViewCodeFixes} language={language} />}
        </div>
      </div>
    </CardContent>
  </Card>
  );
};
export const ActionPlan = ({ actionPlan, auditId, canViewCodeFixes = false, marketIntelligence = null, miActions = null, geoCheck = null, language = 'en' }) => {
  const t = useTranslations(language);
  if (!actionPlan && !miActions?.length && !geoCheck) return null;

  // Merge MI actions + GEO actions into one sorted list (P0 > P1 > P2)
  const allActions = [];
  if (miActions?.length > 0) {
    miActions.forEach(a => allActions.push({ ...a, _source: 'mi' }));
  }
  if (geoCheck?.action_items?.length > 0) {
    geoCheck.action_items.forEach(a => allActions.push({
      ...a,
      priority: a.priority_level || 1,
      _source: 'geo',
    }));
  }
  allActions.sort((a, b) => (a.priority || 99) - (b.priority || 99));

  return (
    <div data-testid="action-plan-section">

      {/* GEO Visibility Section */}
      {geoCheck && <GeoVisibilitySection geoCheck={geoCheck} language={language} />}

      {/* Unified Action Items (MI + GEO — sorted by priority) */}
      {allActions.length > 0 && (
        <div className="mb-6 space-y-3">
          {allActions.map((action, i) => (
            <MIActionItem key={i} action={action} index={i} auditId={auditId} canViewCodeFixes={canViewCodeFixes} language={language} />
          ))}
        </div>
      )}

      {/* Quick Wins */}
      {actionPlan?.quick_wins?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-yellow-400" />
            <h4 className="text-sm font-semibold text-yellow-400 uppercase tracking-widest">{t('quick_wins')}</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {actionPlan.quick_wins.map((w, i) => (
              <span key={i} className="text-xs bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 px-3 py-1.5 rounded-sm">{w}</span>
            ))}
          </div>
        </div>
      )}

      {/* Next 30 Days */}
      {actionPlan?.next_30_days?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-blue-400" />
            <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-widest">{t('next_30_days')}</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {actionPlan.next_30_days.map((item, i) => (
              <span key={i} className="text-xs bg-blue-500/10 border border-blue-500/20 text-blue-300 px-3 py-1.5 rounded-sm">{item}</span>
            ))}
          </div>
        </div>
      )}

      {/* Legacy top_actions */}
      {actionPlan?.top_actions?.length > 0 && (
        <div className="space-y-2 mt-4">
          {actionPlan.top_actions.map(action => (
            <ActionItem key={action.id} action={action} auditId={auditId} canViewCodeFixes={canViewCodeFixes} language={language} />
          ))}
        </div>
      )}
    </div>
  );
};
